import { PageProps } from '@/types';
import { Reel } from '@/types/reel.type';
import { SelectOption } from '@/Components/Select';
import {
    DATE_RANGE_QUERY_PARAM,
    PerformanceQueryParam,
} from '@/types/performance.type';

//
// NOTE: CONSIDER USING TYPES FROM "@/types/flow.type" INSTEAD
//

//
// TODO: REVIEW & REMOVE DUPLICATED TYPES
//

export type SelectItem = {
    id: Omit<PerformanceQueryParam, typeof DATE_RANGE_QUERY_PARAM>;
    options: SelectOption[];
};

export type CustomSelectItem = {
    id: typeof DATE_RANGE_QUERY_PARAM;
    Component: React.FC<any>;
};

export const DEFAULT_SELECT_VALUE = 'all';

export const PERFORMANCE_TABLE_COLUMN_FLOW = 'performance/flow';
export const PERFORMANCE_TABLE_COLUMN_STATUS = 'performance/status';
export const PERFORMANCE_TABLE_COLUMN_TYPE = 'performance/type';
export const PERFORMANCE_TABLE_COLUMN_OBJECTIVE = 'performance/objective';
export const PERFORMANCE_TABLE_COLUMN_VISITORS_REACHED =
    'performance/visitors-reached';
export const PERFORMANCE_TABLE_COLUMN_VIDEO_REELS_PLAYED =
    'performance/video-reels-played';
export const PERFORMANCE_TABLE_COLUMN_CLICKS = 'performance/clicks';
export const PERFORMANCE_TABLE_COLUMN_ACTIVATION_URL =
    'performance/activation-url';
export const PERFORMANCE_TABLE_COLUMN_PUBLISHED_AT =
    'performance/published-at';
export const PERFORMANCE_TABLE_COLUMN_IMPRESSIONS =
    'performance/impressions';
export const PERFORMANCE_TABLE_COLUMN_STARTED =
    'performance/started';
export const PERFORMANCE_TABLE_COLUMN_START_RATE =
    'performance/start-rate';
export const PERFORMANCE_TABLE_COLUMN_BUTTON_CLICKS =
    'performance/button-clicks';
export const PERFORMANCE_TABLE_COLUMN_BUTTON_ENGAGEMENT =
    'performance/button-engagement';
export const PERFORMANCE_TABLE_COLUMN_TOTAL_WATCH_TIME_SECONDS =
    'performance/total-watch-time-seconds';

export const columnIds = [
    PERFORMANCE_TABLE_COLUMN_FLOW,
    PERFORMANCE_TABLE_COLUMN_STATUS,
    PERFORMANCE_TABLE_COLUMN_TYPE,
    PERFORMANCE_TABLE_COLUMN_OBJECTIVE,
    PERFORMANCE_TABLE_COLUMN_VISITORS_REACHED,
    PERFORMANCE_TABLE_COLUMN_VIDEO_REELS_PLAYED,
    PERFORMANCE_TABLE_COLUMN_CLICKS,
    PERFORMANCE_TABLE_COLUMN_ACTIVATION_URL,
    PERFORMANCE_TABLE_COLUMN_PUBLISHED_AT,
    PERFORMANCE_TABLE_COLUMN_IMPRESSIONS,
    PERFORMANCE_TABLE_COLUMN_STARTED,
    PERFORMANCE_TABLE_COLUMN_START_RATE,
    PERFORMANCE_TABLE_COLUMN_BUTTON_CLICKS,
    PERFORMANCE_TABLE_COLUMN_BUTTON_ENGAGEMENT,
    PERFORMANCE_TABLE_COLUMN_TOTAL_WATCH_TIME_SECONDS,
] as const;

export type ColumnId = (typeof columnIds)[number];

export type PerformancePageProps = PageProps<{
    flows: FlowData[];
    stats: WidgetData[];
    flowMetrics?: FlowMetrics[];
    overallStatistics: OverallStatistics;
    reels: Reel[];
    hiddenPerformanceColumns: string[];
    showMetricDefinitions: boolean;
    pagination?: {
        current_page: number;
        total: number;
        per_page: number;
        total_pages: number;
    };
}>;

export type OverallStatistics = {
    flow_button_clicks: number;
    flow_impressions: number;
    flow_started: number;
    total_watch_time_seconds: number;
    unique_visitors: number;
    videos_played: number;
};

export type FlowMetrics = {
    flow_id: number;
    impressions: number;
    started: number;
    videos_played: number;
    button_clicks: number;
    start_rate: number;
    total_watch_time_seconds: number;
    button_engagement: number;
};

export type WidgetData = {
    visitors?: { label: string; value: number; bgColor: string; color: string };
    reelsPlayed?: {
        label: string;
        value: number;
        bgColor: string;
        color: string;
    };
    totalTime?: {
        label: string;
        value: string;
        bgColor: string;
        color: string;
    };
    clicks?: { label: string; value: number; bgColor: string; color: string };
};

export interface FlowTableData {
    id: number;
    avatar: string;
    flowName: string;
    playerType: 'Overlay' | 'Inline';
    flowObjective: string[];
    visitorsReached: number;
    reelsPlayed: number;
    clicks: number;
    goalsMade: number;
    activation: string[];
}

export interface FlowData {
    id: number;
    name: string;
    config: {
        type: 'Inline' | 'Overlay';
        objectives: string[];
        page: string;
    };
    stats_since: {
        play_time: number;
        reach: number;
        plays: number;
        clicks: number;
    };
    flowMetrics?: FlowMetrics;
    status: string;
    page: string;
    type: string;
    number: number;
    targetNodeSelector: string | null;
    player: Player;
    eyeCatcher: EyeCatcher[] | null | [];
    flowName: string;
    playerType: 'Overlay' | 'Inline';
    flowObjective: string;
    visitorsReached: number;
    reelsPlayed: number;
    clicks: number;
    goalsMade: number;
    activation: string[];
    newSummaryData: NewSummaryData;
    avatar: string;
    created_at: string;
    updated_at: string;
    deleted_at: string;
    published_at: string;
    first_published_at: string;
}

export interface ActionPayloadLink {
    href: string;
    target: string;
}

export interface ActionPayloadVideo {
    flowId: string;
    videoId: string;
}

export interface FlowEditorButton {
    id: string;
    label: string;
    clicked: string;
    actionType: string;
    actionPayload: ActionPayloadVideo | ActionPayloadLink;
    style?:
        | any[]
        | {
              borderColor: string;
              borderWidth: string;
              borderRadius: string;
              fontSize: string;
              background: string;
          };
}

export interface Video {
    id: string;
    url: string;
    imageUrl: string;
    text: string;
    title: string;
    buttons: FlowEditorButton[];
    subtitles: string;
    captions: string;
    authorName?: string;
    authorRole?: string;
    views: number;
    timeWatched: string;
    form?: {};
    videoSummaryData: VideoSummaryData;
}

interface Player {
    style: any[];
    theme: string;
    videos: Video[];
    brandingVariant: number;
}

interface EyeCatcherTitle {
    default: string;
    template: string;
}

interface EyeCatcherUrlMap {
    gif: string;
    mp4: string;
    vtt?: string;
}

export interface EyeCatcher {
    id: string;
    style: any[];
    title: EyeCatcherTitle;
    urlMap: EyeCatcherUrlMap;
    views: number;
}

type FlowHistory = {
    created: string;
    createdBy: string;
    lastModified: string;
    modifiedBy: string;
    lastPublished: string;
    publishedBy: string;
};

type VideoSummaryData = {
    reelTitle: string;
    reelLength: string;
    views: number;
    timeWatched: string;
};

// export type FlowSummaryData = {
//     summary: string;
//     activation: {
//         page: string;
//         eyeCatcherTiming: string;
//     };
//     flowObjective: string;
//     flowGoals: {
//         totalGoals: number;
//         hits: number;
//     };
//     overallPerformance: {
//         visitorsReached: number;
//         reelsPlayed: number;
//         clicks: number;
//     };
//     visibility: string;
//     history: FlowHistory;
// };

export type NewSummaryData = {
    summary: string;
    activation: string[];
    flowObjectives: string[];
    overallPerformance: {
        visitorsReached: number;
        totalReelViews: number;
        clicks: number;
    };
    status: string;
    history: {
        created: {
            date: string;
            time: string;
            by: string;
        };
        lastModified: {
            date: string;
            time: string;
            by: string;
        };
        lastPublished: {
            date: string;
            time: string;
            by: string;
        };
    };
};
